import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './NotificationsDrawer.module.css';
import hubStyles from './HomeHub.module.css';
import { ModuleSkeletonList, EmptyState } from './HomeHubParts';

function formatDateSafe(dateValue) {
  try {
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function compactText(value) {
  if (!value) return '';
  return String(value).trim();
}

const IconClose = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconBell = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
);

const IconShield = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2l8 4v6c0 5-3.3 9.4-8 10-4.7-.6-8-5-8-10V6l8-4Z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

export default function NotificationsDrawer({
  open,
  onClose,
  invitations,
  invitationsLoading,
  accountActions,
  accountActionsLoading,
  actionLoadingIds,
  onAcceptInvitation,
  onDeclineInvitation,
  onActionCta,
  onViewInvitations,
}) {
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchEndY, setTouchEndY] = useState(null);
  const closeBtnRef = useRef(null);

  const list = useMemo(() => (Array.isArray(invitations) ? invitations : []), [invitations]);
  const actionsList = useMemo(() => (Array.isArray(accountActions) ? accountActions : []), [accountActions]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      // Focus the close button for fast keyboard escape/close
      setTimeout(() => closeBtnRef.current?.focus?.(), 0);
    }
  }, [open]);

  const handleTouchStart = (e) => {
    if (!open) return;
    const y = e.targetTouches?.[0]?.clientY;
    if (typeof y === 'number') {
      setTouchStartY(y);
      setTouchEndY(null);
    }
  };

  const handleTouchMove = (e) => {
    if (!open) return;
    const y = e.targetTouches?.[0]?.clientY;
    if (typeof y === 'number') {
      setTouchEndY(y);
    }
  };

  const handleTouchEnd = () => {
    if (!open || typeof touchStartY !== 'number' || typeof touchEndY !== 'number') return;
    const delta = touchEndY - touchStartY;
    if (delta > 55) {
      onClose?.();
    }
  };

  return (
    <>
      <div
        className={`${styles.overlay} ${open ? styles.overlayOpen : ''}`}
        onClick={onClose}
        aria-hidden={!open}
        style={{ zIndex: 7000 }}
      />

      <aside
        className={`${styles.drawer} ${open ? styles.drawerOpen : ''}`}
        aria-label="Notifications"
        role="dialog"
        aria-modal="true"
        style={{ zIndex: 7100 }}
      >
        <div
          className={styles.grabHeader}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className={styles.grabHandle} aria-hidden="true" />
          <div className={styles.headerRow}>
            <div className={styles.headerTitle}>
              <span className={styles.headerIcon} aria-hidden="true">
                <IconBell />
              </span>
              Notifications
            </div>
            <button
              ref={closeBtnRef}
              type="button"
              className={`${hubStyles.iconButton} ${styles.closeBtn}`}
              onClick={onClose}
              aria-label="Close notifications"
            >
              <IconClose />
            </button>
          </div>
        </div>

        <div className={styles.body}>
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <span className={styles.headerIcon} aria-hidden="true">
                  <IconShield />
                </span>
                Account actions
              </div>
            </div>
            {accountActionsLoading ? (
              <ModuleSkeletonList rows={2} />
            ) : actionsList.length === 0 ? (
              <EmptyState
                title="Nothing urgent"
                body="Account actions will appear here when something needs your attention."
              />
            ) : (
              <div className={styles.accountActionsList}>
                {actionsList.map((action) => {
                  const id = action?.id;
                  const title = compactText(action?.title) || 'Account action';
                  const body = compactText(action?.body);
                  const ctas = Array.isArray(action?.ctas) ? action.ctas : [];
                  return (
                    <div key={id || title} className={styles.accountActionRow}>
                      <div className={styles.accountActionMain}>
                        <div className={styles.accountActionTitle}>{title}</div>
                        {body ? <div className={styles.accountActionBody}>{body}</div> : null}
                      </div>
                      {ctas.length ? (
                        <div className={styles.accountActionCtas}>
                          {ctas.map((cta, idx) => {
                            const label = compactText(cta?.label) || 'Open';
                            const btnClass = idx === 0 ? styles.actionBtn : styles.actionBtnSecondary;
                            return (
                              <button
                                key={`${label}-${idx}`}
                                type="button"
                                className={btnClass}
                                onClick={() => onActionCta?.(cta)}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>Invitations</div>
              <button type="button" className={styles.linkButton} onClick={onViewInvitations}>
                View all
              </button>
            </div>

            {invitationsLoading ? (
              <ModuleSkeletonList rows={3} />
            ) : list.length === 0 ? (
              <EmptyState
                title="No invitations"
                body="When someone invites you to connect, it will appear here."
              />
            ) : (
              <div className={styles.invList}>
                {list.map((inv) => {
                  const id = inv?._id;
                  const name = compactText(inv?.name) || 'Invitation';
                  const company = compactText(inv?.company);
                  const role = compactText(inv?.role);
                  const message = compactText(inv?.message);
                  const date = formatDateSafe(inv?.createdAt);
                  const busy = !!(id && actionLoadingIds?.has?.(id));

                  return (
                    <div key={id || `${name}-${date}`} className={styles.invRow}>
                      <div className={styles.invMain}>
                        <div className={styles.invTop}>
                          <div className={styles.invName}>{name}</div>
                          {date ? <div className={styles.invDate}>{date}</div> : null}
                        </div>
                        <div className={styles.invMeta}>
                          {company ? <span>{company}</span> : null}
                          {company && role ? <span className={hubStyles.dot} /> : null}
                          {role ? <span>{role}</span> : null}
                        </div>
                        {message ? <div className={styles.invMessage}>{message}</div> : null}
                      </div>

                      <div className={styles.invActions}>
                        <button
                          type="button"
                          className={styles.actionBtn}
                          onClick={() => id && onAcceptInvitation?.(id)}
                          disabled={!id || busy}
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          className={styles.actionBtnSecondary}
                          onClick={() => id && onDeclineInvitation?.(id)}
                          disabled={!id || busy}
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </aside>
    </>
  );
}

